// src/components/job-engine/useJobs.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { getJobs } from "@/api/jobEngine";
import type { Job, JobsQuery } from "@/api/jobEngine";

export interface UseJobsResult {
  items: Job[];
  setItems: React.Dispatch<React.SetStateAction<Job[]>>;
  totalCount: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useJobs(params: JobsQuery): UseJobsResult {
  const [items, setItems] = useState<Job[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setIsLoading(true);
    setError(null);
    getJobs(params, ctrl.signal)
      .then((r) => {
        if (ctrl.signal.aborted) return;
        setItems(r.items ?? []);
        setTotalCount(r.totalCount ?? 0);
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (isAxiosError(err)) setError(err.response?.data?.title ?? "Failed to load jobs");
        else setError("An unexpected error occurred");
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setIsLoading(false);
      });
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params), tick]);

  return { items, setItems, totalCount, isLoading, error, refetch };
}
